import { useEffect, useState } from 'react';
import { HTTPError } from 'ky';
import * as Styled from './styles';
import { Table, toastify } from '#ui-kit';
import { TableCellAlign } from '#ui-kit.types';
import { Head } from '#components';
import { HeadUnderlineVariant } from '#components.types';
import { Deal, dealsService, DealsPage, PayoutStatus, payoutStatuses } from '#services/deals';

const statusLabels: Record<PayoutStatus, string> = {
  PENDING: 'Ожидает завершения',
  PROCESSING: 'В обработке',
  FAILED: 'Ошибка',
  COMPLETED: 'Завершена',
  REFUNDING: 'Возврат',
  CANCELLED: 'Отменена',
};

const formatDate = (value: string) => new Date(value).toLocaleString('ru-RU');

export default function Deals() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [status, setStatus] = useState<PayoutStatus | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [result, setResult] = useState<DealsPage>();
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string>();
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    dealsService.list({ page, size, status, sortOrder }).then((data) => {
      if (active) setResult(data);
    }).catch(() => {
      if (active) toastify.error('Не удалось загрузить сделки');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [page, size, status, sortOrder, refresh]);

  const retry = async (deal: Deal) => {
    setRetrying(deal.dealId);
    try {
      await dealsService.retry(deal.dealId);
      toastify.success('Сделка завершена');
      setRefresh((value) => value + 1);
    } catch (error) {
      const code = error instanceof HTTPError ? error.response.status : undefined;
      toastify.error(code === 409 ? 'Сделка уже завершена или пока не готова к завершению'
        : code === 422 ? 'Не удалось завершить сделку: ошибка обработки'
        : 'Не удалось завершить сделку');
    } finally {
      setRetrying(undefined);
    }
  };

  const header = ['Сделка', 'Платёж', 'Выплата', 'Создана', 'Обновлена', 'Статус', 'Ошибка', 'Действие'];
  return (
    <>
      <Head label="Сделки" underlineVariant={HeadUnderlineVariant.Primary} />
      <Styled.Filters>
        <label>Статус
          <select value={status || ''} onChange={(event) => {
            setStatus(event.target.value ? event.target.value as PayoutStatus : undefined);
            setPage(1);
          }}>
            <option value="">Все статусы</option>
            {payoutStatuses.map((value) => <option key={value} value={value}>{statusLabels[value]}</option>)}
          </select>
        </label>
        <label>Дата создания
          <select value={sortOrder} onChange={(event) => {
            setSortOrder(event.target.value as 'asc' | 'desc');
            setPage(1);
          }}>
            <option value="desc">Сначала новые</option>
            <option value="asc">Сначала старые</option>
          </select>
        </label>
      </Styled.Filters>
      <Table
        loading={loading}
        header={{ cells: Object.fromEntries(header.map((name, index) => [index, { align: TableCellAlign.Left, children: name }])) }}
        rows={(result?.data || []).map((deal) => ({
          cells: Object.fromEntries([
            deal.dealId, deal.paymentId, deal.e2cPaymentId || '—',
            formatDate(deal.createdAt), formatDate(deal.updatedAt),
            statusLabels[deal.status], deal.errorMessage || '—',
            <Styled.RetryButton type="button" disabled={!!retrying || loading} onClick={() => retry(deal)}>
              {retrying === deal.dealId ? 'Завершение…' : 'Завершить'}
            </Styled.RetryButton>,
          ].map((value, index) => [index, { align: TableCellAlign.Left, children: value }])),
        }))}
        currentPage={page}
        pageSize={size}
        totalRows={result?.total || 0}
        onPageChange={(nextPage, nextSize) => { setPage(nextPage); setSize(nextSize); }}
      />
    </>
  );
}
